import { provideHttpClient, withXhr } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig, PreloadAllModules } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withXhr()),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withPreloading(PreloadAllModules),
            // Angular 22 changes the default to 'always'. Pinned to the pre-v22
            // 'emptyOnly' so the framework hop does not alter how nested
            // :projectId routes inherit parent params (§10.3).
            withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'enterprise', // 'vertical', 'horizontal', 'compact', 'classy'
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),

        // SEC-03 Phase 4 — the CDK top layer is now ADOPTED. The Phase 2a
        // opt-out (`OVERLAY_DEFAULT_CONFIG` with `usePopover: false`) was
        // removed here; CDK 22.1.4 still defaults `usePopover ?? true`
        // (re-verified in the shipped `_overlay-module-chunk.mjs`), so every
        // overlay THAT OPTS IN now lives in the browser top layer, where
        // z-index no longer orders it against the app's high-z surfaces.
        //
        // ⚠️ "Opts in" is doing real work in that sentence — there are two
        // exceptions, and neither is hypothetical:
        //   1. CDK falls back to `usePopover = false` when the Popover API is
        //      absent: `if (!doc.body || !('showPopover' in doc.body))`. Those
        //      overlays are ordered by z-index exactly as before.
        //   2. Per-overlay config (and `cdkConnectedOverlayUsePopover`) can
        //      still opt an individual overlay out.
        //
        // ⚠️ Adoption is also NOT uniform in the DOM. Two insertion strategies
        // ship: 'global' keeps the host inside `.cdk-overlay-container`
        // (matTooltip, mat-menu, MatDialog), while 'inline' inserts the host
        // immediately after the ORIGIN element (mat-select, mat-autocomplete,
        // mat-timepicker) — so a select panel lands inside its own
        // `<mat-select>`, in the app's component subtree. Verified at runtime.
        // Anything reasoning about overlay placement must handle both.
        //
        // ⚠️ Do NOT reintroduce this provider to 'fix' a stacking bug — that
        // reverts a completed phase. See §10.4 and the Phase 4 results.
        //
        // NOTE: `provideZoneChangeDetection()` is supplied by `main.ts`, NOT
        // here, and its placement there is deliberate — v21+'s (re-verified
        // in 22.1.4, core.mjs:2438) `internalCreateApplication` builds
        // [provideZonelessChangeDetectionInternal(), errorHandler, ...providers],
        // so main.ts's position reproduces v20's zone-before-app ordering.
        // Moving it into this array would reorder its ENVIRONMENT_INITIALIZER
        // entries relative to Fuse's provideEnvironmentInitializer.
        // ⚠️ Consequence: from v21 onward, **absence** of that provider is an active
        // choice of ZONELESS, not a neutral default — so `appConfig` on its own
        // is a zoneless config. Anything bootstrapping from `appConfig` without
        // main.ts (a test harness, an SSR entry point) runs zoneless silently,
        // with no error and no build failure. Relevant to Phase 13.
    ],
};
